"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import { Button, cx } from "@/components/ui";

function Field({ label, error, ...rest }: { label: string; error?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = rest.name;
  return (
    <label className="flex w-full flex-col gap-1" htmlFor={id}>
      <span className="type-label-m text-fg-2">{label}</span>
      <input
        id={id}
        className={cx(
          "rounded-md border bg-surface px-4 py-3 type-body-m text-fg outline-none placeholder:text-fg-3",
          "focus:border-2 focus:border-line-focus focus:shadow-glow-playhead",
          error ? "border-2 border-danger" : "border-line-default",
        )}
        {...rest}
      />
    </label>
  );
}

export function AuthForm({ mode }: { mode: "sign-in" | "sign-up" }) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    const email = String(f.get("email"));
    const password = String(f.get("password"));
    const res =
      mode === "sign-up"
        ? await authClient.signUp.email({ email, password, name: String(f.get("name")) })
        : await authClient.signIn.email({ email, password });
    setBusy(false);
    if (res.error) return setError(res.error.message ?? "Something went wrong");
    router.push("/library");
    router.refresh();
  }

  async function sendMagic(e: React.MouseEvent) {
    const form = (e.currentTarget as HTMLElement).closest("form")!;
    const email = String(new FormData(form).get("email") ?? "");
    if (!email) return setError("Enter your email first");
    const r = await authClient.signIn.magicLink({ email, callbackURL: "/library" });
    if (r.error) setError(r.error.message ?? "Could not send link");
    else setMagicSent(true);
  }

  return (
    // method="post": if it's submitted before React hydrates (slow phone over Tailscale), the browser must
    // not fall back to a GET that puts the email + password in the URL.
    <form onSubmit={onSubmit} method="post" className="flex w-full flex-col gap-5">
      {mode === "sign-up" && <Field label="Your name" name="name" required autoComplete="name" placeholder="Chris Garcia" />}
      <Field label="Email" name="email" type="email" required autoComplete="email" placeholder="you@studio.com" />
      <Field
        label="Password"
        name="password"
        type="password"
        required
        minLength={8}
        autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
        placeholder="At least 8 characters"
        error={error ?? undefined}
      />
      {error && <p className="-mt-3 type-body-s text-danger" role="alert">{error}</p>}
      <Button type="submit" disabled={busy} className="w-full">
        {busy ? "One sec…" : mode === "sign-up" ? "Create account" : "Sign in"}
      </Button>
      {mode === "sign-in" && (
        <button type="button" onClick={sendMagic} className="type-label-m text-fg-3 hover:text-fg">
          {magicSent ? "Check your inbox (dev: the server console) for the link" : "Email me a magic link instead"}
        </button>
      )}
      <p className="text-center type-body-s text-fg-3">
        {mode === "sign-up" ? (
          <>Already have an account? <Link className="text-fg-brand hover:underline" href="/sign-in">Sign in</Link></>
        ) : (
          <>New to BOUNCE? <Link className="text-fg-brand hover:underline" href="/sign-up">Create an account</Link></>
        )}
      </p>
    </form>
  );
}
